// src/utils/cesdkUtils.js
import supabase from './supabaseUtils'; // Import supabase for database and storage operations
import { EDITOR_CONFIG } from '../config/config'; // Import EDITOR_CONFIG for version checking
import { v4 as uuidv4 } from 'uuid'; // For generating unique file names

/**
 * Safely check if a method exists in CESDK before calling it
 */
export const safeCallCESDK = (cesdk, methodPath, args = [], fallback = null) => {
  if (!cesdk) return fallback && fallback();
  
  const parts = methodPath.split('.');
  let current = cesdk;
  
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (current[part] === undefined) {
      console.warn(`CESDK method ${methodPath} not available in version ${EDITOR_CONFIG.version}`);
      return fallback && fallback();
    }
    
    if (i === parts.length - 1) {
      if (typeof current[part] !== 'function') {
        console.warn(`CESDK path ${methodPath} is not a function in version ${EDITOR_CONFIG.version}`);
        return fallback && fallback();
      }
      return current[part](...args);
    }
    
    current = current[part];
  }
  
  return fallback && fallback();
};

/**
 * Check CESDK version compatibility
 */
export const checkCESDKVersion = (cesdk) => {
  if (!cesdk || !cesdk.version) return false;
  
  const currentVersion = cesdk.version;
  const expectedVersion = EDITOR_CONFIG.version;
  
  if (currentVersion !== expectedVersion) {
    console.warn(`CESDK version mismatch. Expected: ${expectedVersion}, Found: ${currentVersion}`);
    return false;
  }
  
  return true;
};

/**
 * Update the "Person" placeholder with the primary image URL
 */
export const updatePlaceholder = async (cesdk, primaryImageUrl, placeholderName) => {
  if (!cesdk || !primaryImageUrl) {
    console.warn("CESDK or primaryImageUrl not available for updatePlaceholder");
    return;
  }

  const scene = cesdk.engine.scene.get();
  const pages = cesdk.engine.block.getChildren(scene);
  const blocks = pages.flatMap(page => cesdk.engine.block.getChildren(page));

  for (const blockId of blocks) {
    const blockName = cesdk.engine.block.getName(blockId);
    const blockType = cesdk.engine.block.getType(blockId);

    if (blockName === placeholderName && blockType === "//ly.img.ubq/graphic") {
      try {
        let fillId = cesdk.engine.block.getFill(blockId);

        if (!fillId) {
          fillId = cesdk.engine.block.createFill("//ly.img.ubq/fill/image");
          cesdk.engine.block.setFillEnabled(fillId, true);
          cesdk.engine.block.setEnum(fillId, "fill/type", "//ly.img.ubq/fill/image");
          cesdk.engine.block.setFill(blockId, fillId);
          console.log(`Created new ImageFill with ID ${fillId} for block ${blockId} (${blockName})`);
        }

        await new Promise(resolve => setTimeout(resolve, 100)); // sync delay

        cesdk.engine.block.setSourceSet(fillId, "fill/image/sourceSet", [
          {
            uri: primaryImageUrl,
            width: blockName === "Background" ? 1920 : 1080,
            height: blockName === "Background" ? 1080 : 720, 
          },
        ]);
         // 🔥 Key line: maintain aspect ratio and prioritize height 
         cesdk.engine.block.setEnum(fillId, "fill/image/mode", "fit"); 

         // Optional: lock aspect ratio of the block to prevent warping
        cesdk.engine.block.setBool(blockId, "constraints/aspectRatio/locked", true);

        console.log(`Updated ${blockName} placeholder with primary image: ${primaryImageUrl}`);

        const fillType = cesdk.engine.block.getEnum(fillId, "fill/type");
        console.log(`Fill type after update for block ${blockId} (${blockName}): ${fillType}`);
      } catch (error) {
        console.error(`Failed to update ${blockName} placeholder: ${error.message}`);
      }
    }
  }
};

/**
 * Save the scene to Supabase
 */
/**
 * Save the scene to Supabase
 */
export async function saveSceneToSupabase(cesdk, templateName, primaryImageUrl) {
  if (!cesdk?.engine?.scene) {
    console.error("CESDK or engine is not initialized in saveSceneToSupabase");
    return { data: null, error: new Error("CESDK or engine not initialized") };
  }

  const { data: { user }, error: authError } = await supabase.auth.getUser();
  if (authError || !user) {
    console.error("No authenticated user found:", authError);
    return { data: null, error: new Error("User not authenticated") };
  }

  let existingTemplate = null;
  try {
    const { data, error } = await supabase
      .from('templates')
      .select('*')
      .eq('name', templateName)
      .eq('user_id', user.id)
      .maybeSingle();
    if (error) return { data: null, error };
    existingTemplate = data;
  } catch (error) {
    return { data: null, error };
  }

  await updatePlaceholder(cesdk, primaryImageUrl, "Person");

  const scene = cesdk.engine.scene.get();
  const pages = cesdk.engine.block.getChildren(scene);
  const blocks = pages.flatMap(page => cesdk.engine.block.getChildren(page));
  const placeholderData = [];
  const seenNames = new Set();

  for (const blockId of blocks) {
    const blockName = cesdk.engine.block.getName(blockId);
    const blockType = cesdk.engine.block.getType(blockId);
    const isPlaceholder = cesdk.engine.block.isPlaceholderEnabled(blockId);

    if (isPlaceholder && !seenNames.has(blockName)) {
      seenNames.add(blockName);
      const entry = { id: blockId, name: blockName, type: blockType };

      if (blockType === "//ly.img.ubq/graphic") {
        try {
          let fillId = cesdk.engine.block.getFill(blockId); // Changed to let
          let fillType = fillId ? cesdk.engine.block.getEnum(fillId, "fill/type") : null;
          let sourceSet = fillId ? cesdk.engine.block.getSourceSet(fillId, "fill/image/sourceSet") : null;

          if (!fillType) {
            const newFillId = cesdk.engine.block.createFill("//ly.img.ubq/fill/image");
            cesdk.engine.block.setFillEnabled(newFillId, true);
            cesdk.engine.block.setEnum(newFillId, "fill/type", "//ly.img.ubq/fill/image");
            cesdk.engine.block.setFill(blockId, newFillId);
            fillType = "//ly.img.ubq/fill/image";
            fillId = newFillId; // Now this reassignment works
            cesdk.engine.block.setSourceSet(newFillId, "fill/image/sourceSet", [
              { uri: primaryImageUrl, width: 1080, height: 720 },
            ]);
          }

          entry.fillType = fillType;
          entry.defaultContent = sourceSet?.[0]?.uri || primaryImageUrl;
        } catch (error) {
          console.error(`Failed to process graphic block ${blockId}: ${error.message}`);
          entry.fillType = "//ly.img.ubq/fill/image";
          entry.defaultContent = primaryImageUrl;
        }
      } else if (blockType === "//ly.img.ubq/text") {
        entry.defaultContent = cesdk.engine.block.getString(blockId, "text/text") || "";
      }

      placeholderData.push(entry);
    }
  }

  const sceneString = await cesdk.engine.scene.saveToString();
  const sceneFileName = `${uuidv4()}.scene`;

  const { error: uploadError } = await supabase.storage
    .from('playmakerpics-images')
    .upload(`scenes/${sceneFileName}`, sceneString, {
      contentType: 'text/plain',
      upsert: true,
      cacheControl: 'no-cache',
    });

  if (uploadError) {
    return { data: null, error: uploadError };
  }

  const sceneUrl = supabase.storage
    .from('playmakerpics-images')
    .getPublicUrl(`scenes/${sceneFileName}`).data.publicUrl;

  const thumbnailBlob = await cesdk.engine.block.export(scene, "image/png", {
    targetWidth: 400,
    targetHeight: 300,
    dpi: 72,
    exportArea: "design",
  });

  const thumbnailFileName = `${uuidv4()}-thumbnail.png`;
  const { error: thumbnailError } = await supabase.storage
    .from('playmakerpics-images')
    .upload(`thumbnails/${thumbnailFileName}`, thumbnailBlob, {
      contentType: 'image/png',
      upsert: true,
    });

  const thumbnailUrl = thumbnailError
    ? 'https://picsum.photos/400/300'
    : supabase.storage.from('playmakerpics-images').getPublicUrl(`thumbnails/${thumbnailFileName}`).data.publicUrl;

  let data, error;
  if (existingTemplate?.id) {
    ({ data, error } = await supabase
      .from('templates')
      .update({
        name: templateName,
        scene_url: sceneUrl,
        thumbnail_url: thumbnailUrl,
        placeholder_data: placeholderData,
        updated_at: new Date().toISOString(),
      })
      .eq('id', existingTemplate.id)
      .select());
  } else {
    ({ data, error } = await supabase
      .from('templates')
      .insert([{
        name: templateName,
        scene_url: sceneUrl,
        thumbnail_url: thumbnailUrl,
        placeholder_data: placeholderData,
        user_id: user.id,
      }])
      .select());
  }

  return error ? { data: null, error } : { data, error: null };
}

/**
 * Create a page block with standard dimensions
 */
export const createStandardPage = (cesdk, dimensions = { width: 1920, height: 1080 }) => {
  if (!cesdk || !cesdk.engine || !cesdk.engine.block) {
    console.error("Cannot create page: CESDK engine not available");
    return null;
  }
  
  try {
    const page = cesdk.engine.block.create('//ly.img.ubq/page');
    cesdk.engine.block.setWidth(page, dimensions.width);
    cesdk.engine.block.setHeight(page, dimensions.height);
    return page;
  } catch (error) {
    console.error("Error creating standard page:", error);
    return null;
  }
};

/**
 * Load a specific template into the CESDK scene
 */
export const loadTemplate = async (cesdk, templateId) => {
  if (!cesdk?.engine?.scene) {
    console.error("❌ CESDK engine or scene is not initialized.");
    return false;
  }

  console.log('loadTemplate called with:', typeof templateId === 'object' ? { 
    id: templateId.id, 
    hasSceneUrl: !!templateId.scene_url,
    sceneUrl: templateId.scene_url?.substring(0, 100) + '...'
  } : templateId);

  try {
    // First try to get the template data if a template ID was provided
    let sceneUrl = null;
    if (typeof templateId === 'string') {
      try {
        // Check if the template ID is already a URL
        if (templateId.startsWith('http')) {
          sceneUrl = templateId;
          console.log('Using URL directly:', sceneUrl);
        } else {
          // First try to get the template from the database
          const { data: template, error } = await supabase
            .from('templates')
            .select('scene_url')
            .eq('id', templateId)
            .single();
          
          if (error) {
            console.warn('Error fetching template from DB:', error);
          }
          
          if (template && template.scene_url) {
            sceneUrl = template.scene_url;
            console.log('Found template scene URL:', sceneUrl);
          } else {
            // Fallback to the old path format
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
            sceneUrl = `${supabaseUrl}/storage/v1/object/public/playmakerpics-images/scenes/${templateId}.scene`;
            console.log('Using fallback scene URL path:', sceneUrl);
          }
        }
      } catch (dbError) {
        console.warn('Error fetching template from database:', dbError);
        // Fallback to the old path format
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        sceneUrl = `${supabaseUrl}/storage/v1/object/public/playmakerpics-images/scenes/${templateId}.scene`;
        console.log('Using fallback scene URL path after DB error:', sceneUrl);
      }
    } else if (typeof templateId === 'object' && templateId.scene_url) {
      // If the templateId parameter is actually a template object with scene_url
      sceneUrl = templateId.scene_url;
      console.log('Using provided scene URL from template object:', sceneUrl);
    }

    if (!sceneUrl) {
      throw new Error('Could not determine scene URL for template');
    }

    console.log('Attempting to fetch from URL:', sceneUrl);
    const response = await fetch(sceneUrl, { 
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    
    if (!response.ok) {
      console.error('Fetch response not OK:', {
        status: response.status,
        statusText: response.statusText,
        url: sceneUrl
      });
      throw new Error(`Failed to fetch scene: ${response.status} ${response.statusText}`);
    }
    
    console.log('Fetch successful, getting text content...');
    const sceneText = await response.text();
    console.log('Scene text received, length:', sceneText.length);
    
    console.log('Loading scene from string...');
    await cesdk.engine.scene.loadFromString(sceneText);
    console.log('✅ Scene successfully loaded from string');
    
    return true;
  } catch (error) {
    console.error("❌ Error loading template:", error);
    return false;
  }
};

/**
 * Export a template with replacement data
 */
export const exportTemplate = async (cesdk, templateId, replacementData) => {
  if (!cesdk?.engine?.scene) {
    console.error("❌ CESDK engine or scene is not initialized.");
    return null;
  }

  if (typeof templateId !== 'string' || !/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(templateId)) {
    console.error("❌ Invalid templateId:", templateId);
    return null;
  }

  try {
    // Get current user ID from Supabase
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    
    if (!userId) {
      console.warn("No user ID available from session, using 'anonymous' for export path");
    }
    
    // Load the template into the scene
    const loaded = await loadTemplate(cesdk, templateId);
    if (!loaded) throw new Error("Failed to load template");

    const scene = cesdk.engine.scene.get();
    if (!scene) throw new Error("No active scene loaded");

    const blocks = getAllBlocksInScene(cesdk, scene);
    for (const [key, value] of Object.entries(replacementData)) {
      const blockId = blocks.find(b => cesdk.engine.block.getName(b) === key);
      if (blockId) {
        const type = cesdk.engine.block.getType(blockId);
        if (type === '//ly.img.ubq/text') {
          cesdk.engine.block.setString(blockId, "text/text", value);
          console.log(`Replaced text in block ${blockId} with: ${value}`);
        } else if (type === '//ly.img.ubq/graphic' && value.startsWith('http')) {
          const fillId = cesdk.engine.block.createFill('//ly.img.ubq/fill/image');
          cesdk.engine.block.setFillEnabled(fillId, true);
          cesdk.engine.block.setEnum(fillId, 'fill/type', '//ly.img.ubq/fill/image');
          cesdk.engine.block.setFill(blockId, fillId);
          
          cesdk.engine.block.setSourceSet(fillId, "fill/image/sourceSet", [
            { uri: value, width: 1080, height: 720 }
          ]);
          
          // Ensure aspect ratio is maintained with "fit" mode
          cesdk.engine.block.setEnum(fillId, "fill/image/mode", "fit");
          
          // Lock aspect ratio of the block to prevent warping
          cesdk.engine.block.setBool(blockId, "constraints/aspectRatio/locked", true);
          
          console.log(`Set image fill in block ${blockId} with: ${value} using fit mode`);
        }
      }
    }

    const pages = cesdk.engine.block.getChildren(scene).filter(block => cesdk.engine.block.getType(block) === '//ly.img.ubq/page');
    if (pages.length > 0) {
      const page = pages[0];
      cesdk.engine.block.setWidth(page, 1920);
      cesdk.engine.block.setHeight(page, 1080);
      console.log(`Set page size to 1920x1080 for block ${page}`);
    }

    const exportBlob = await cesdk.engine.block.export(scene, "image/png", {
      targetWidth: 1920,
      targetHeight: 1080,
      dpi: 72,
      exportArea: "design"
    });

    // Include user ID in the export path
    const userPath = userId || 'anonymous';
    const exportFileName = `${templateId}-export-${Date.now()}.png`;
    const { data, error: exportError } = await supabase.storage
      .from('playmakerpics-images')
      .upload(`exports/${userPath}/${exportFileName}`, exportBlob, {
        upsert: true,
        cacheControl: 'no-cache',
        contentType: 'image/png'
      });

    if (exportError) throw new Error(`Export upload failed: ${exportError.message}`);

    const exportUrl = `${supabase.storage
      .from('playmakerpics-images')
      .getPublicUrl(`exports/${userPath}/${exportFileName}`).data.publicUrl}?t=${Date.now()}`;
    console.log("✅ Exported template successfully:", exportUrl);
    return exportUrl;
  } catch (error) {
    console.error("❌ Export failed:", error);
    return null;
  }
};

/**
 * Helper function to get all blocks in the scene
 */
export const getAllBlocksInScene = (cesdk, scene) => {
  const result = [];
  const pages = cesdk.engine.block.getChildren(scene) || [];
  pages.forEach(page => {
    result.push(page);
    const collectChildren = (blockId) => {
      try {
        const children = cesdk.engine.block.getChildren(blockId) || [];
        children.forEach(child => {
          result.push(child);
          collectChildren(child);
        });
      } catch (err) {
        console.warn(`Error collecting children for block ${blockId}:`, err);
      }
    };
    collectChildren(page);
  });
  return result.filter(blockId => cesdk.engine.block.getType(blockId) !== '//ly.img.ubq/scene');
};

/**
 * Save a template to Supabase (metadata and scene data)
 */
export const saveTemplate = async (templateData) => {
  try {
    const { data, error } = await supabase
      .from('templates')
      .insert([templateData])
      .select();
    if (error) throw error;
    console.log("Template metadata saved to Supabase:", data);
    return data[0];
  } catch (error) {
    console.error("Error saving template metadata to Supabase:", error);
    throw error;
  }
};

/**
 * Get a specific template by ID from Supabase
 */
export const getTemplate = async (templateId) => {
  try {
    const { data, error } = await supabase
      .from('templates')
      .select('*')
      .eq('id', templateId)
      .single();
    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error fetching template from Supabase:", error);
    throw error;
  }
};

/**
 * Get all templates from Supabase
 */
export const getAllTemplates = async () => {
  try {
    const { data, error } = await supabase
      .from('templates')
      .select('*');
    if (error) throw error;
    console.log("getAllTemplates raw data:", data, "error:", error);
    // Sort data by created_at if it exists, otherwise sort by id
    const sortedData = data.sort((a, b) => {
      if (a.created_at && b.created_at) {
        return new Date(b.created_at) - new Date(a.created_at);
      }
      return b.id - a.id;
    });
    return sortedData || [];
  } catch (error) {
    console.error("Error fetching templates from Supabase:", error);
    return [];
  }
};

/**
 * Get an order by ID from Supabase
 */
export const getOrderById = async (orderId) => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .eq('id', orderId)
      .single();
    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error fetching order from Supabase:", error);
    throw error;
  }
};

/**
 * Get background assets from Supabase storage
 */
export const getBackgroundAssets = async () => {
  try {
    const { data, error } = await supabase.storage
      .from('playmakerpics-images')
      .list('backgrounds', { limit: 100, offset: 0 });
    if (error) throw error;

    const assets = data
      .filter(file => file.name !== '.emptyFolderPlaceholder')
      .map(file => {
        const encodedFileName = encodeURIComponent(file.name);
        const uri = `${supabase.storage.from('playmakerpics-images').getPublicUrl(`backgrounds/${encodedFileName}`).data.publicUrl}?t=${Date.now()}`;
        return { id: file.name, uri };
      });
    return assets;
  } catch (error) {
    console.error("Error fetching background assets from Supabase:", error);
    return [];
  }
};

/**
 * Get athlete images from Supabase storage
 */
export const getAthleteImages = async () => {
  const path = 'athletes'; // Updated path to match Supabase folder
  console.log(`Fetching athlete images from path: ${path}`);
  try {
    const { data, error } = await supabase.storage.from('playmakerpics-images').list(path, {
      limit: 100,
      offset: 0,
      sortBy: { column: 'name', order: 'asc' }
    });
    if (error) {
      console.error(`Error listing athlete images from ${path}:`, error);
      throw error;
    }
    console.log(`Athlete files found in ${path}:`, data);

    const assets = data
      .filter(file => file.name !== '.emptyFolderPlaceholder' && /\.(jpg|jpeg|png|gif)$/i.test(file.name))
      .map(file => {
        const encodedFileName = encodeURIComponent(file.name);
        const fullPath = `${path}/${encodedFileName}`;
        const { publicUrl } = supabase.storage.from('playmakerpics-images').getPublicUrl(fullPath).data;
        return { id: file.name, uri: publicUrl, thumbUri: publicUrl };
      });
    return assets;
  } catch (error) {
    console.error("❌ Error fetching athlete images:", error);
    return [];
  }
};

/**
 * Upload an image to Supabase storage
 */
export const uploadImage = async (file, path) => {
  try {
    const fileName = `${Date.now()}-${file.name}`;
    const { data, error } = await supabase.storage
      .from('playmakerpics-images')
      .upload(`${path}/${fileName}`, file, {
        upsert: true,
        cacheControl: 'no-cache'
      });
    if (error) throw error;

    const uri = `${supabase.storage.from('playmakerpics-images').getPublicUrl(`${path}/${fileName}`).data.publicUrl}?t=${Date.now()}`;
    const thumbUri = uri; // For simplicity, using the same URI for thumbnail; adjust if needed
    return { uri, thumbUri };
  } catch (error) {
    console.error("Error uploading image to Supabase:", error);
    throw error;
  }
};

/**
 * Fetch background images with test asset included
 */
export const fetchBackgroundImages = async () => {
  console.log("📡 Fetching background images from Supabase...");
  try {
    const { data, error } = await supabase.storage
      .from('playmakerpics-images')
      .list('backgrounds', { limit: 100, offset: 0 });
    if (error) throw error;

    const assets = data
      .filter(file => file.name !== '.emptyFolderPlaceholder')
      .map(file => {
        const encodedFileName = encodeURIComponent(file.name);
        const uri = `${supabase.storage.from('playmakerpics-images').getPublicUrl(`backgrounds/${encodedFileName}`).data.publicUrl}?t=${Date.now()}`;
        return {
          id: `bg-${file.name.replace(/\//g, '-')}`,
          label: file.name.split('.')[0] || "Background Image",
          kind: 'image',
          meta: {
            uri,
            thumbUri: uri,
            blockType: '//ly.img.ubq/graphic',
            fillType: '//ly.img.ubq/fill/image',
            shapeType: '//ly.img.ubq/shape/rect',
            width: 1920,
            height: 1080,
            mimeType: file.name.match(/\.(png|jpg)$/i) ? "image/png" : "image/jpeg",
            cacheBuster: Date.now(),
          },
        };
      });

    // Filter out assets with invalid URIs
    const validAssets = [];
    for (const asset of assets) {
      try {
        const response = await fetch(asset.meta.uri, { method: 'HEAD' });
        if (response.ok) {
          validAssets.push(asset);
        } else {
          console.warn(`Skipping background asset ${asset.id}: URI is invalid or inaccessible (${asset.meta.uri})`);
        }
      } catch (err) {
        console.warn(`Skipping background asset ${asset.id}: Failed to verify URI (${asset.meta.uri})`, err);
      }
    }

    // Add test asset
    validAssets.push({
      id: 'test-background',
      label: 'Test Background',
      kind: 'image',
      meta: {
        uri: 'https://picsum.photos/1920/1080',
        thumbUri: 'https://picsum.photos/200/200',
        blockType: '//ly.img.ubq/graphic',
        fillType: '//ly.img.ubq/fill/image',
        shapeType: '//ly.img.ubq/shape/rect',
        width: 1920,
        height: 1080,
        mimeType: "image/jpeg",
      },
    });

    console.log("Background assets:", validAssets);
    return { assets: validAssets, total: validAssets.length, currentPage: 0, nextPage: undefined };
  } catch (error) {
    console.error("❌ Error fetching background images:", error);
    return {
      assets: [{
        id: 'test-background',
        label: 'Test Background',
        kind: 'image',
        meta: {
          uri: 'https://picsum.photos/1920/1080',
          thumbUri: 'https://picsum.photos/200/200',
          blockType: '//ly.img.ubq/graphic',
          fillType: '//ly.img.ubq/fill/image',
          shapeType: '//ly.img.ubq/shape/rect',
          width: 1920,
          height: 1080,
          mimeType: "image/jpeg",
        },
      }],
      total: 1,
      currentPage: 0,
      nextPage: undefined,
    };
  }
};

export const createPlaceholderBlock = async (cesdk, config) => {
  const {
    name,
    width = 1080,
    height = 720,
    posX = 0,
    posY = 0,
    type = '//ly.img.ubq/graphic'
  } = config;

  try {
    const block = cesdk.engine.block.create(type);
    cesdk.engine.block.setName(block, name);
    cesdk.engine.block.setWidth(block, width);
    cesdk.engine.block.setHeight(block, height);
    cesdk.engine.block.setPositionX(block, posX);
    cesdk.engine.block.setPositionY(block, posY);

    if (type === '//ly.img.ubq/graphic') {
      // Create and set shape
      const shapeId = cesdk.engine.block.createShape('//ly.img.ubq/shape/rect');
      cesdk.engine.block.setShape(block, shapeId);

      // Create and prepare fill
      const fillId = cesdk.engine.block.createFill('//ly.img.ubq/fill/image');
      cesdk.engine.block.setFillEnabled(fillId, true);
      cesdk.engine.block.setEnum(fillId, 'fill/type', '//ly.img.ubq/fill/image');
      cesdk.engine.block.setFill(block, fillId);
      
      // Set fill mode to "fit" to maintain aspect ratio
      cesdk.engine.block.setEnum(fillId, "fill/image/mode", "fit");
      
      // Lock aspect ratio of the block to prevent warping
      cesdk.engine.block.setBool(block, "constraints/aspectRatio/locked", true);
    }

    return block;
  } catch (error) {
    console.error(`Failed to create placeholder block ${name}:`, error);
    throw error;
  }
};

/**
 * Fetch athlete images with test asset included
 */
export const fetchAthleteImages = async () => {
  console.log("📡 Fetching athlete images from Supabase...");
  try {
    const { data, error } = await supabase.storage
      .from('playmakerpics-images')
      .list('athletes', { limit: 100, offset: 0 });
    if (error) throw error;

    const assets = data
      .filter(file => file.name !== '.emptyFolderPlaceholder')
      .map(file => {
        const encodedFileName = encodeURIComponent(file.name);
        const uri = `${supabase.storage.from('playmakerpics-images').getPublicUrl(`athletes/${encodedFileName}`).data.publicUrl}?t=${Date.now()}`;
        return {
          id: `athlete-${file.name}`,
          label: file.name.split('.')[0] || "Athlete Image",
          kind: 'image',
          meta: {
            uri,
            thumbUri: uri,
            blockType: '//ly.img.ubq/graphic',
            fillType: '//ly.img.ubq/fill/image',
            shapeType: '//ly.img.ubq/shape/rect',
            //width: 1080,
            //height: 720,
            mimeType: "image/png",
            cacheBuster: Date.now(),
          },
        };
      });

    // Filter out assets with invalid URIs
    const validAssets = [];
    for (const asset of assets) {
      try {
        const response = await fetch(asset.meta.uri, { method: 'HEAD' });
        if (response.ok) {
          validAssets.push(asset);
        } else {
          console.warn(`Skipping athlete asset ${asset.id}: URI is invalid or inaccessible (${asset.meta.uri})`);
        }
      } catch (err) {
        console.warn(`Skipping athlete asset ${asset.id}: Failed to verify URI (${asset.meta.uri})`, err);
      }
    }

    // Add test asset
    validAssets.push({
      id: 'test-athlete',
      label: 'Test Athlete',
      kind: 'image',
      meta: {
        uri: 'https://picsum.photos/1080/720',
        thumbUri: 'https://picsum.photos/200/200',
        blockType: '//ly.img.ubq/graphic',
        fillType: '//ly.img.ubq/fill/image',
        shapeType: '//ly.img.ubq/shape/rect',
        //width: 1080,
        //height: 720,
        mimeType: "image/png",
      },
    });

    console.log("Athlete assets:", validAssets);
    return { assets: validAssets, total: validAssets.length, currentPage: 0, nextPage: undefined };
  } catch (error) {
    console.error("❌ Error fetching athlete images:", error);
    return {
      assets: [{
        id: 'test-athlete',
        label: 'Test Athlete',
        kind: 'image',
        meta: {
          uri: 'https://picsum.photos/1080/720',
          thumbUri: 'https://picsum.photos/200/200',
          blockType: '//ly.img.ubq/graphic',
          fillType: '//ly.img.ubq/fill/image',
          shapeType: '//ly.img.ubq/shape/rect',
          width: 1080,
          height: 720,
          mimeType: "image/png",
        },
      }],
      total: 1,
      currentPage: 0,
      nextPage: undefined,
    };
  }
};